import { observer } from 'mobx-lite';
import { useEffect, useRef } from 'react';
import { MainStoreContextItem } from '../../Stores/MainStore';
import { FaceSdkStoreContextItem } from '../../Stores/FaceSdkStore';
import { FaceOverlayCanvas } from './FaceOverlayCanvas';
//import { OverlaySvg } from './VideoOverlay';
import { QualityProgressOverlay } from './QualityProgressOverlay';
import { EventOverlay } from '../EventOverlay';
import { WebSocketStatusIndicator } from '../WebSocketStatusIndicator';

// const StyledVideo = styled('video')(({ theme }) => ({
//     width: '100%',
//     height: 'auto',
//     borderRadius: theme.shape.borderRadius, // Will be handled by parent container
//     backgroundColor: '#000',
//     border: `1px solid ${theme.palette.divider}`,
//     aspectRatio: '16 / 9',
//     display: 'block',
//     objectFit: 'cover',
// }))

interface VideoDisplayProps {
    stream: MediaStream | null;
}

export const VideoDisplay = observer(({ stream }: VideoDisplayProps) => {
    const store = MainStoreContextItem.useStore();
    const faceSdkStore = FaceSdkStoreContextItem.useStore();

    // Use local ref and sync with store
    const localVideoRef = useRef<HTMLVideoElement>(null);
    const { faceSdk, sdkInitializing } = faceSdkStore;
    const flipVideoHorizontally = store.settingsStore.effectiveFlipHorizontally;

    // Sync local ref with store ref on mount and when video element changes
    useEffect(() => {
        if (localVideoRef.current) {
            // Update the store's ref to point to our current video element
            store.mediaDeviceStore.videoElementRef.current = localVideoRef.current;
            console.log('VideoDisplay: Synced video element ref with store');
        }
    }, [store.mediaDeviceStore]);

    // Also sync when the video element itself changes (e.g., after stream changes)
    useEffect(() => {
        if (localVideoRef.current && store.mediaDeviceStore.videoElementRef.current !== localVideoRef.current) {
            store.mediaDeviceStore.videoElementRef.current = localVideoRef.current;
            console.log('VideoDisplay: Re-synced video element ref with store after stream change');
        }
    }, [stream, store.mediaDeviceStore]);

    // Effect to handle mounting/unmounting and ensure face detection stops when component unmounts
    useEffect(() => {
        // Component mounted, ensure we have a fresh start
        return () => {
            // Component unmounting, stop face detection
            console.log('VideoDisplay: Component unmounting, stopping face detection');
            store.faceServiceStore.stopFaceDetection();
        };
    }, [store.faceServiceStore]);

    useEffect(() => {
        const videoElement = localVideoRef.current;
        if (videoElement) {
            // Check if srcObject is already set to this stream to avoid unnecessary resets
            if (stream && videoElement.srcObject !== stream) {
                videoElement.srcObject = stream;
                videoElement.play().catch((error: unknown) => console.error('Error playing video:', error));
            } else if (!stream && videoElement.srcObject) {
                videoElement.srcObject = null;
            }
        }
    }, [stream]);

    // Consolidated effect to handle face detection initialization
    useEffect(() => {
        const videoElement = localVideoRef.current;

        console.log('VideoDisplay: Face detection effect triggered', {
            hasVideoElement: !!videoElement,
            hasStream: !!stream,
            hasFaceSdk: !!faceSdk,
            sdkInitializing,
            videoReadyState: videoElement?.readyState,
            isDetecting: store.faceServiceStore.isDetecting,
        });

        if (
            videoElement &&
            stream &&
            faceSdk &&
            !sdkInitializing &&
            videoElement.readyState >= 3 && // HAVE_FUTURE_DATA or better
            !store.faceServiceStore.isDetecting
        ) {
            console.log('VideoDisplay: Starting face detection');
            // Set video element reference and start detection
            store.faceServiceStore.setVideoElement(videoElement);
            store.faceServiceStore.autoStartDetection();
        }
    }, [stream, faceSdk, sdkInitializing, store.faceServiceStore]);

    // Handle video ready state changes to ensure we start detection when video is ready
    useEffect(() => {
        const videoElement = localVideoRef.current;

        if (!videoElement) return;

        const handleLoadedData = () => {
            console.log('VideoDisplay: Video loadeddata event');
            if (stream && faceSdk && !sdkInitializing && !store.faceServiceStore.isDetecting) {
                console.log('VideoDisplay: Starting face detection from loadeddata');
                store.faceServiceStore.setVideoElement(videoElement);
                store.faceServiceStore.autoStartDetection();
            }
        };

        const handleCanPlay = () => {
            console.log('VideoDisplay: Video canplay event');
            if (stream && faceSdk && !sdkInitializing && !store.faceServiceStore.isDetecting) {
                console.log('VideoDisplay: Starting face detection from canplay');
                store.faceServiceStore.setVideoElement(videoElement);
                store.faceServiceStore.autoStartDetection();
            }
        };

        videoElement.addEventListener('loadeddata', handleLoadedData);
        videoElement.addEventListener('canplay', handleCanPlay);

        return () => {
            videoElement.removeEventListener('loadeddata', handleLoadedData);
            videoElement.removeEventListener('canplay', handleCanPlay);
        };
    }, [stream, faceSdk, sdkInitializing, store.faceServiceStore]);

    return (
        <div
            style={{
                width: '100%',
                height: '100vh', // Use most of viewport height
                //maxHeight: 'calc(100vh - 120px)', // Leave space for header and status
                position: 'relative', // Important for positioning children absolutely
                backgroundColor: '#000',
                overflow: 'hidden',
                display: 'flex', // Added flex to help with sizing
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                style={{
                    zIndex: 0, // Below overlays
                    width: '100%',
                    height: '100%', // Make video fill the container
                    objectFit: 'cover', // This will scale and crop to fill
                    display: 'block',
                    border: 'none',
                    backgroundColor: 'transparent',
                    transform: flipVideoHorizontally ? 'scaleX(-1)' : 'none',
                }}
            />

            <FaceOverlayCanvas />

            {/* <OverlaySvg /> */}

            <QualityProgressOverlay />

            <EventOverlay
                eventThemesStore={store.eventThemesStore}
                eventClassification={store.faceServiceStore.lastEventClassification}
                isVisible={!!store.faceServiceStore.lastEventClassification}
                onOverlayActiveChanged={(active) => store.faceServiceStore.setEventOverlayActive(active)}
            />
            <WebSocketStatusIndicator />

            {/* Compositor keep-alive: a 2px element running a continuous compositor-only
                animation so Chromium keeps scheduling frames and the <video> doesn't park
                on a long-idle foreground (crbug.com/1401352). Negligible cost; complements
                the frame-stall watchdog in MediaDeviceStore, which handles the screen-off
                case where the whole WebView is suspended. */}
            <style>{'@keyframes camKeepAlive{0%{transform:translateZ(0) scale(1)}50%{transform:translateZ(0) scale(1.0002)}100%{transform:translateZ(0) scale(1)}}'}</style>
            <div
                aria-hidden
                style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 2,
                    height: 2,
                    opacity: 0.01,
                    pointerEvents: 'none',
                    willChange: 'transform',
                    animation: 'camKeepAlive 2s linear infinite',
                }}
            />
        </div>
    );
});

VideoDisplay.displayName = 'VideoDisplay';
